import { useState, type FormEvent } from 'react';
import { api, daysUntil, dueText, initialsOf, type Todo } from '../api';
import { useAuth, useData } from '../App';
import { DeleteButton, Empty, Icon, IconButton } from '../ui';

type Filter = 'min' | 'allt' | 'oudhlutad';

export default function Verkefni() {
  const { todos, users, refresh } = useData();
  const { user } = useAuth();

  const [filter, setFilter] = useState<Filter>('min');
  const [openId, setOpenId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Partial<Todo>>({});
  const [saving, setSaving] = useState(false);

  /* new-task form */
  const [text, setText] = useState('');
  const [due, setDue] = useState('');
  const [assignee, setAssignee] = useState<string>(String(user?.id ?? ''));
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const open = todos.filter((t) => !t.done);
  const mine = open.filter((t) => t.assigned_user_id === user?.id);
  const unassigned = open.filter((t) => t.assigned_user_id === null);

  const shown =
    filter === 'min'
      ? todos.filter((t) => t.assigned_user_id === user?.id)
      : filter === 'oudhlutad'
        ? todos.filter((t) => t.assigned_user_id === null)
        : todos;

  const nameOf = (id: number | null) => users.find((u) => u.id === id)?.display_name ?? null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.addTodo({
        text: text.trim(),
        due_date: due || null,
        assigned_user_id: assignee ? Number(assignee) : null,
        notes: notes.trim(),
      });
      setText('');
      setDue('');
      setNotes('');
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  function beginEdit(t: Todo) {
    setOpenId(t.id);
    setEditId(t.id);
    setDraft({
      text: t.text,
      due_date: t.due_date,
      assigned_user_id: t.assigned_user_id,
      notes: t.notes,
    });
  }

  function cancelEdit() {
    setEditId(null);
    setDraft({});
  }

  async function saveEdit() {
    if (editId === null || !String(draft.text ?? '').trim()) return;
    setSaving(true);
    try {
      await api.updateTodo(editId, {
        text: String(draft.text).trim(),
        due_date: draft.due_date || null,
        assigned_user_id: draft.assigned_user_id ?? null,
        notes: String(draft.notes ?? '').trim(),
      });
      cancelEdit();
      await refresh();
    } finally {
      setSaving(false);
    }
  }

  async function toggle(id: number, done: boolean) {
    await api.updateTodo(id, { done: !done });
    await refresh();
  }

  async function remove(id: number) {
    await api.delTodo(id);
    if (openId === id) setOpenId(null);
    if (editId === id) cancelEdit();
    await refresh();
  }

  return (
    <div className="view">
      <div className="g-two">
        <section className="card col-8">
          <div className="card-h">
            <h2>Verkefnalisti</h2>
            <span className="lbl">
              {mine.length} á þér · {open.length} opin alls
            </span>
          </div>

          <div className="filters">
            {(
              [
                ['min', 'Mín verkefni', mine.length],
                ['allt', 'Allt', todos.length],
                ['oudhlutad', 'Óúthlutað', unassigned.length],
              ] as const
            ).map(([key, label, n]) => (
              <button
                key={key}
                type="button"
                className={`filt${filter === key ? ' on' : ''}`}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
              >
                {label}
                <span className="n">{n}</span>
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <Empty
              title={
                filter === 'min'
                  ? 'Ekkert á þér núna'
                  : filter === 'oudhlutad'
                    ? 'Allt er úthlutað'
                    : 'Engin verkefni'
              }
              body={
                filter === 'min'
                  ? 'Taktu að þér verkefni undir „Óúthlutað“ eða skráðu nýtt.'
                  : 'Skrifaðu niður það sem þarf að klára og settu nafn á það.'
              }
            />
          ) : (
            <div className="todos">
              {shown.map((t) => {
                const d = daysUntil(t.due_date);
                const done = !!t.done;
                const who = nameOf(t.assigned_user_id);
                const expanded = openId === t.id;
                const editing = editId === t.id;
                const urgency = done ? '' : d === null ? '' : d < 0 ? ' over' : d <= 3 ? ' soon' : '';

                return (
                  <div key={t.id} className={`todo-wrap${expanded ? ' open' : ''}`}>
                    <div className={`todo${done ? ' done' : ''}`}>
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={done}
                        aria-label={t.text}
                        className={`tick${done ? ' on' : ''}`}
                        onClick={() => void toggle(t.id, done)}
                      />

                      <button
                        type="button"
                        className="tx tx-btn"
                        aria-expanded={expanded}
                        onClick={() => setOpenId(expanded ? null : t.id)}
                      >
                        <span className="tx-main">
                          {t.text}
                          {t.notes && <span className="has-note" title="Hefur athugasemd">•</span>}
                        </span>
                        {t.due_date && !done && (
                          <span className={`due-inline${urgency}`}>{dueText(t.due_date)}</span>
                        )}
                      </button>

                      <span className={`chev${expanded ? ' up' : ''}`} aria-hidden="true">
                        <Icon.chev />
                      </span>

                      <select
                        className="assign"
                        value={t.assigned_user_id ?? ''}
                        onChange={(e) =>
                          void api
                            .updateTodo(t.id, {
                              assigned_user_id: e.target.value ? Number(e.target.value) : null,
                            })
                            .then(refresh)
                        }
                        aria-label={who ? `Úthlutað: ${who}` : 'Óúthlutað'}
                        title={who ?? 'Óúthlutað'}
                      >
                        <option value="">—</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {initialsOf(u.display_name)}
                          </option>
                        ))}
                      </select>

                      <DeleteButton label={`Eyða: ${t.text}`} onClick={() => void remove(t.id)} />
                    </div>

                    {expanded && (
                      <div className="todo-body">
                        {editing ? (
                          <div className="form">
                            <div className="fld">
                              <label className="lbl">Verkefni</label>
                              <input
                                type="text"
                                value={String(draft.text ?? '')}
                                onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                              />
                            </div>
                            <div className="fr">
                              <div className="fld">
                                <label className="lbl">Skiladagur</label>
                                <input
                                  type="date"
                                  value={draft.due_date ?? ''}
                                  onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
                                />
                              </div>
                              <div className="fld">
                                <label className="lbl">Á hvern</label>
                                <select
                                  value={draft.assigned_user_id ?? ''}
                                  onChange={(e) =>
                                    setDraft({
                                      ...draft,
                                      assigned_user_id: e.target.value ? Number(e.target.value) : null,
                                    })
                                  }
                                >
                                  <option value="">Óúthlutað</option>
                                  {users.map((u) => (
                                    <option key={u.id} value={u.id}>
                                      {u.display_name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="fld">
                              <label className="lbl">Athugasemdir</label>
                              <textarea
                                rows={8}
                                placeholder="Minnispunktar, tenglar, hvað var ákveðið…"
                                value={String(draft.notes ?? '')}
                                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                              />
                            </div>
                            <div className="row" style={{ justifyContent: 'flex-end', gap: 10 }}>
                              <button type="button" className="btn ghost sm" onClick={cancelEdit}>
                                Hætta við
                              </button>
                              <button
                                type="button"
                                className="btn sm"
                                onClick={() => void saveEdit()}
                                disabled={saving || !String(draft.text ?? '').trim()}
                              >
                                {saving ? 'Vista…' : 'Vista'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {t.notes ? (
                              <p className="note-body">{t.notes}</p>
                            ) : (
                              <p className="note-empty">Engar athugasemdir skráðar.</p>
                            )}
                            <div className="row" style={{ justifyContent: 'space-between' }}>
                              <span className="lbl">
                                {who ? `Á ${who}` : 'Óúthlutað'}
                                {t.due_date ? ` · skil ${dueText(t.due_date)}` : ''}
                              </span>
                              <IconButton label="Breyta verkefni" onClick={() => beginEdit(t)}>
                                <Icon.pencil />
                              </IconButton>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="card col-4">
          <div className="card-h">
            <h2>Nýtt verkefni</h2>
          </div>
          <form className="form" onSubmit={submit}>
            <div className="fld">
              <label className="lbl" htmlFor="t-text">Verkefni</label>
              <input
                id="t-text"
                type="text"
                placeholder="Hvað þarf að gera?"
                value={text}
                onChange={(e) => setText(e.target.value)}
                required
              />
            </div>

            <div className="fr">
              <div className="fld">
                <label className="lbl" htmlFor="t-due">Skiladagur</label>
                <input id="t-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
              </div>
              <div className="fld">
                <label className="lbl" htmlFor="t-who">Á hvern</label>
                <select id="t-who" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                  <option value="">Óúthlutað</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.display_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="fld">
              <label className="lbl" htmlFor="t-notes">Athugasemdir</label>
              <textarea
                id="t-notes"
                rows={5}
                placeholder="Minnispunktar, tenglar, hvað var ákveðið…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <button className="btn" type="submit" disabled={busy || !text.trim()}>
              Bæta við
            </button>
          </form>

          <p style={{ marginTop: 18, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
            Smelltu á heiti verkefnis til að fletta því niður — þar sérðu athugasemdirnar og getur
            breytt öllu. Punktur við heitið þýðir að athugasemd sé skráð.
          </p>
        </section>
      </div>
    </div>
  );
}
